import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import {
  ClaimantBeneficiary,
  ClaimantEarning,
  ClaimantOccupation,
  ClaimantProfile,
  ClaimantResponse,
} from '../../../../models/api/view/claim/details/claimant/claimant-response';
import { BaseComponent } from '../../../base/base.component';
import { HicnLabelComponent } from '../../../core/labels/hicn-label.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { SsnLabelComponent } from '../../../core/labels/ssn-label.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-claimant',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TableComponent,
    ColumnComponent,
    RouterModule,
    CardComponent,
    PageHeaderComponent,
    LinkLabelComponent,
    HicnLabelComponent,
    SsnLabelComponent,
  ],
  template: `
    <app-page-header label="Claimant" />
    <div class="tw-flex tw-flex-col tw-gap-8 tw-pb-4 tw-pt-4">
      <app-card label="Profile">
        <div class="tw-flex tw-gap-8">
          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="profile" [showEmpty]="false">
              <app-detail-item
                label="Name"
                name="claimantFullName"
                classes="tw-basis-1/5"
              />

              @if (showClaimantRestrictedField('ClaimantSSN')) {
                <app-detail-item
                  label="SSN"
                  labelCase="upper"
                  name="claimantSSN"
                  classes="tw-basis-1/5"
                >
                  <ng-template #value>
                    <app-ssn-label
                      [value]="profile?.claimantSSN"
                      [canUnmaskSsn]="profile?.isUnmaskSSN"
                    />
                  </ng-template>
                </app-detail-item>
              }
              @if (showClaimantRestrictedField('Sex')) {
                <app-detail-item label="Sex" name="claimantSex" />
              }
              @if (showClaimantRestrictedField('MaritalStatus')) {
                <app-detail-item label="Marital Status" name="maritalStatus" />
              }
              @if (showClaimantRestrictedField('DateOfBirth')) {
                <app-detail-item
                  label="Date of Birth"
                  name="birthDate"
                  format="d"
                />
              }
              @if (showClaimantRestrictedField('DeathDate')) {
                <app-detail-item
                  label="Date of Death"
                  name="deathDate"
                  format="d"
                  [showEmpty]="false"
                />
              }
            </app-detail-panel>
          </div>
          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="profile">
              @if (showClaimantRestrictedField('HomePhone')) {
                <app-detail-item
                  label="Home Phone"
                  name="homePhone"
                  format="p"
                />
              }
              @if (showClaimantRestrictedField('WorkPhone')) {
                <app-detail-item
                  label="Work Phone"
                  name="workPhone"
                  format="p"
                />
              }
              @if (showClaimantRestrictedField('ClaimantMobilePhone')) {
                <app-detail-item
                  label="Cell Phone"
                  name="mobilePhone"
                  format="p"
                />
              }
              @if (showClaimantRestrictedField('Age')) {
                <app-detail-item
                  label="Age at DOL"
                  name="age"
                  labelCase="none"
                />
              }
              @if (showClaimantRestrictedField('CurrentAge')) {
                <app-detail-item label="Current Age" name="currentAge" />
              }
            </app-detail-panel>
          </div>
          <div class="tw-basis-1/3">
            <app-detail-panel [dataSource]="profile">
              @if (showClaimantRestrictedField('Email')) {
                <app-detail-item label="Email" name="email" />
              }
              @if (showClaimantRestrictedField('HomeAddress')) {
                <app-detail-item label="Address" name="address" />
              }
              @if (showClaimantRestrictedField('County')) {
                <app-detail-item label="County" name="county" />
              }
              @if (showClaimantRestrictedField('Country')) {
                <app-detail-item label="Country" name="country" />
              }
              @if (profile?.isUnmaskSSN && profile?.claimantHICN) {
                <app-detail-item label="HICN" format="s" labelCase="none">
                  <ng-template #value>
                    <app-hicn-label [value]="profile.claimantHICN" />
                  </ng-template>
                </app-detail-item>
              } @else {
                <app-detail-item
                  label="HICN"
                  format="s"
                  name="claimantHICN"
                  labelCase="none"
                />
              }
              @if (showClaimantRestrictedField('MedicareBeneficiary')) {
                <app-detail-item
                  label="Medicare beneficiary"
                  name="medicareBeneficiaryStatusLabel"
                  labelCase="none"
                />
              }
            </app-detail-panel>
          </div>
        </div>
      </app-card>
      <app-card label="Occupation">
        <div class="tw-flex tw-gap-8">
          <app-detail-panel [dataSource]="occupations" class="tw-flex-1">
            @if (showClaimantRestrictedField('ClassCode')) {
              <app-detail-item label="Class Code" name="classCode" />
            }
            @if (showClaimantRestrictedField('Occupation')) {
              <app-detail-item label="Occupation" name="occupation" />
            }
            @if (showClaimantRestrictedField('Demand')) {
              <app-detail-item label="Demand" name="demand" />
            }
            @if (showClaimantRestrictedField('DeptName')) {
              <app-detail-item label="Department Name" name="deptName" />
            }
            @if (showClaimantRestrictedField('DateOfHire')) {
              <app-detail-item
                label="Date of Hire"
                name="dateOfHire"
                format="d"
              />
            }
            @if (showClaimantRestrictedField('DateTerminated')) {
              <app-detail-item
                label="Date Terminated"
                name="dateTerminated"
                format="d"
              />
            }
            <app-detail-item label="Employment type" name="employmentType" />
            <app-detail-item
              label="Other weekly payments"
              name="otherWeeklyPayments"
              format="c"
            />
          </app-detail-panel>

          <app-detail-panel [dataSource]="occupations" class="tw-flex-1">
            @if (showClaimantRestrictedField('ServiceLength')) {
              <app-detail-item
                label="Service Length"
                name="serviceLength"
                format="sl"
              />
            }
            @if (showClaimantRestrictedField('UnionNumber')) {
              <app-detail-item label="Union Number" name="unionNumber" />
            }
            @if (showClaimantRestrictedField('WageBase')) {
              <app-detail-item label="base rate" name="wageBase" />
            }
            @if (showClaimantRestrictedField('BaseInterval')) {
              <app-detail-item label="base interval" name="baseInterval" />
            }
            @if (showClaimantRestrictedField('NumHrsWpw')) {
              <app-detail-item label="hours per week" name="numHrsWpw" />
            }
          </app-detail-panel>

          <app-detail-panel [dataSource]="occupations" class="tw-flex-1">
            <app-detail-item label="AWW weeks" name="awwWeeks" />
            <app-detail-item
              label="Pre-injury AWW"
              name="preInjuryAww"
              format="c"
            />
            <app-detail-item
              label="Method of determining AWW"
              name="methodOfDeterminingAww"
            />
            <app-detail-item
              label="Date disability began"
              name="dateDisabilityBegan"
              format="d"
            />
            <app-detail-item
              label="Jurisdictional def of week"
              name="jurisdictionalDelofWeek"
            />
            <app-detail-item
              label="Current wage"
              name="currentWage"
              format="c"
            />
          </app-detail-panel>
        </div>
      </app-card>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-8 tw-pt-4">
      @if (beneficiaries?.length > 0) {
        <div class="tw-basis-full">
          <app-card label="Beneficiary">
            <app-table
              [dataSource]="beneficiaries"
              [pageSize]="5"
              [recordLabel]="'Beneficiary'"
              [recordLabelPlural]="'Beneficiaries'"
            >
              <app-column name="numbers" label="Numbers" />
              <app-column name="code" label="Code" />
              <app-column name="dateOfBirth" format="d" label="Date of birth" />
              <app-column name="dependency" label="Dependency" />
              <app-column name="gender" label="Gender" />
            </app-table>
          </app-card>
        </div>
      }
      @if (earningAuth) {
        <div class="tw-basis-full">
          <app-card label="Earnings">
            <div class="tw-pb-3">
              Refer to
              <app-link-label [routerLink]="['../payment-history']"
                >Payment History</app-link-label
              >
              to view net benefit information.
            </div>
            <app-table
              [dataSource]="earnings"
              [pageSize]="10"
              [recordLabel]="'History'"
              [recordLabelPlural]="'Histories'"
            >
              <app-column
                name="payrollDate"
                format="d"
                label="Payroll Date"
                width="20%"
              />
              <app-column
                name="payrollNumber"
                label="Payroll Year"
                width="20%"
              />
              <app-column name="periodFrequency" label="Period" width="20%" />
              <app-column name="payrollNumber" label="Period #" width="10%" />
              <app-column
                name="basePay"
                format="c"
                label="Base Pay"
                width="10%"
              />
              <app-column
                name="grossAmount"
                format="c"
                label="Gross Amount"
                width="10%"
              />
              <app-column
                name="spendable"
                format="n"
                label="Spendable"
                width="20%"
              />
              <app-column
                name="hoursWorked"
                format="n"
                label="Hours Worked"
                width="20%"
              />
              <app-column
                name="overtimeHours"
                format="n"
                label="Overtime Hours"
                width="20%"
              />
            </app-table>
          </app-card>
        </div>
      }
    </div>
  `,
  styles: ``,
})
export class ClaimantComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  ClaimantResponse: ClaimantResponse;
  profile: ClaimantProfile;
  beneficiaries: ClaimantBeneficiary[];
  occupations: ClaimantOccupation;
  earnings: ClaimantEarning[];
  earningAuth = false;
  constructor() {
    super();
  }

  showClaimantRestrictedField(fieldKey: string): boolean {
    const restricted = this.session.user?.fieldSecurity?.restrictedFields ?? [];
    return !restricted.includes(fieldKey);
  }

  async ngOnInit() {
    this.earningAuth = this.view.authorize('EmployeeEarnings');
    this.claimRow = this.view.currentClaim();
    this.ClaimantResponse = await this.api.claimant.get(
      getClaimKey(),
      this.claimRow.contractNumber,
      this.earningAuth,
    );
    this.profile = this.ClaimantResponse.profile;
    if (this.profile) {
      const addressParts = [
        this.profile.address,
        this.profile.city,
        this.profile.state,
        this.profile.zip,
      ].filter((part) => part && part.trim());
      this.profile.address = addressParts.join(', ');
      switch (this.profile.medicareBeneficiaryStatus) {
        case 'U':
          this.profile.medicareBeneficiaryStatusLabel = 'Not Matched';
          break;
        case 'Y':
          this.profile.medicareBeneficiaryStatusLabel = 'Yes';
          break;
        case 'N':
        case null:
          this.profile.medicareBeneficiaryStatusLabel = 'Not Checked';
      }
    }
    this.beneficiaries = this.ClaimantResponse.beneficiaries;
    this.occupations = this.ClaimantResponse.occupations;
    this.earnings = this.ClaimantResponse.earnings;
    this.view.hideLoading();
  }
}
